import React, {
  lazy,
  PropsWithChildren,
  Suspense,
  useCallback,
  useContext,
  useEffect
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Container } from "@magnetic/container";
import { Spinner } from "@magnetic/spinner";

import { ErrorBoundary } from "src/components/errorBoundary";
import { AppHeader } from "src/core/appHeader/appHeader";
import { PATHS } from "src/core/constants";
import { PageLayout } from "src/layouts";
import { CaptiveScreen } from "src/layouts/CaptiveLayout/CaptiveScreen";

import { DataProvider } from "./provider/dataProvider";
import { CrossGeoAddOrg } from "./crossGeoAddOrg";
import GlobalHeader from "./globalHeader";
import { Login } from "./login";
import { LoginContext } from "./loginContext";
import { Navbar } from "./navbar";
import { TimezoneProvider } from "./timeZoneProvider";
import { WelcomeScreen } from "./welcomeScreen";

const DevLogin = lazy(() => import("src/core/devLogin"));
const C3Login = lazy(() => import("src/core/c3Login"));

export const Shell = ({
  children
}: PropsWithChildren): React.ReactElement | null => {
  const isDevMode: boolean = window.cnc?.settings?.get("dev");
  const { session, configLimits } = useContext(LoginContext);
  const canDisplayAppHeader =
    window.cnc?.settings?.get("customHeader") ||
    Boolean(session?.isC3Crosslaunch);
  const navigate = useNavigate();
  const location = useLocation();
  const isC3Login: boolean =
    location.pathname === `/${PATHS.LOGIN}` &&
    new URLSearchParams(location.search).has("cl_token");

  const isCrossGeoAddOrg =
    window.cnc?.settings?.get("multiRegionOrgUI") &&
    location.pathname === `/${PATHS.ADD_ORG}`;

  const navigateTo = useCallback(
    (
      path: string,
      queryString: string = "",
      options?: {
        replace?: boolean;
        //eslint-disable-next-line @typescript-eslint/no-explicit-any
        state?: any;
      }
    ) => {
      if (window.location.pathname !== path) {
        navigate(`${path}${queryString}`, options);
      }
    },
    [navigate]
  );

  /**
   * wrapping the navigate calls in useEffect
   * as to not call setState while rendering
   */
  useEffect(() => {
    const PATH_LOGIN = `/${PATHS.LOGIN}`;
    const PATH_LOGIN_DEV = `/${PATHS.DEV_LOGIN}`;
    const PATH_SIGN_UP = "/signup";
    if (!isC3Login) {
      if (!session) {
        navigateTo(
          `${isDevMode ? PATH_LOGIN_DEV : PATH_LOGIN}`,
          location.search,
          {
            state: { location }
          }
        );
      }

      if (session?.newUser || session?.requireSignUp) {
        if (!isCrossGeoAddOrg) {
          navigateTo(PATH_SIGN_UP);
        }
      }
    }
  }, [isDevMode, location, navigateTo, session, isC3Login, isCrossGeoAddOrg]);

  if (isC3Login) {
    return (
      <Suspense fallback={<Spinner />}>
        <C3Login />
      </Suspense>
    );
  }

  if (!session) {
    return isDevMode ? (
      <ErrorBoundary>
        <Suspense fallback={<Spinner />}>
          <DevLogin />
        </Suspense>
      </ErrorBoundary>
    ) : (
      <Login />
    );
  }

  if (isCrossGeoAddOrg && session) {
    return <CrossGeoAddOrg />;
  }

  if (session?.newUser || session?.requireSignUp) {
    return <WelcomeScreen />;
  }

  return (
    <TimezoneProvider>
      <DataProvider configLimits={configLimits}>
        <CaptiveScreen>
          <PageLayout
            banner={canDisplayAppHeader ? <AppHeader /> : <GlobalHeader />}
            sideNav={<Navbar />}
            content={<Container>{children}</Container>}
          />
        </CaptiveScreen>
      </DataProvider>
    </TimezoneProvider>
  );
};
